import { useLoader } from '@/context/Loader';
import { useNotificationsEnabled } from '@/context/Notifications/usePortalNotifications';
import { MarketoFormModal } from '@/features/MarketoForm';
import { Notifications } from '@/features/Notifications';
import withNotificationDataWrapper from '@/features/Notifications/HoC/withNotificationDataWrapper';
import { UnauthorizedMessage } from '@/features/shared/portal';
import withAuthorizationGlobalData from '@/features/shared/portal/UnauthorizedMessage/HoC/withAuthorizationGlobalData';
import { CMS_BASE_URL, SPA_BASE_URL } from '@/utils/constants';
import PrerenderQueue from '@/utils/prerender-queue';
import React, {
  FunctionComponent,
  memo,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { Props } from './GlobalHeader.interface';
// import { useNavigationVisibility } from '@/features/GlobalHeaderContainer/hooks/useNavigationVisibility';

const parseProp = (content: any) => {
  try {
    return JSON.stringify(content || '');
  } catch (e) {
    console.log(':: GlobalHeader:', { e });
    return '{}';
  }
};

type GlobalHeaderProps = Props;

const UnauthorizedMessageOnHeader = withAuthorizationGlobalData(UnauthorizedMessage);

const GlobalHeader: FunctionComponent<PropsWithChildren<GlobalHeaderProps>> = (
  props: PropsWithChildren<GlobalHeaderProps>,
) => {
  const {
    hasProgressBar,
    isTransparent = false,
    menuList,
    loginSection,
    children,
    config,
    marketoConfig,
    elasticsearchConfig,
    labels,
    languageList,
    linkListTitleStyle,
    campaign,
    accountSection,
    appsSection,
    loginClicked,
    logoutClicked,
    datalayerVariant = '',
    showCustomAccountSlotData,
    languageConfig = {},
    sessionConfig = {},
    hideMobileNavigationButton = false,
    searchSection,
    ctaLink,
  } = props;
  const { isLoading } = useLoader();
  const {
    munchkin_id: marketoMunchkinId,
    base_url: marketoBaseUrl,
    recaptcha_sitekey: recaptchaSitekey,
    recaptcha_legal: recaptchaLegal,
    email_fieldname: emailFieldname,
    email_blacklist_error_message: emailBlacklistErrorMessage,
    email_crm_fieldname: emailCRMFieldname,
  } = marketoConfig || {};

  // const { MarketoFormModal, UnauthorizedMessage, Notifications } = useComponents();
  const globalHeaderRef = useRef<any>(null);
  const history = useHistory();
  const [locked, setLocked] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [formModalLinkText, setFormModalLinkText] = useState<string>('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const NotificationDataWrapped = useMemo(() => withNotificationDataWrapper(Notifications), []);
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const notificationAvailabilityStatus = useNotificationsEnabled();

  const notificationConfig = useMemo(
    () => ({
      hasUnreadNotifications,
      notificationsEnabled: notificationAvailabilityStatus.enabled,
    }),
    [hasUnreadNotifications, notificationAvailabilityStatus],
  );

  // const navVisible = useNavigationVisibility();

  useEffect(() => {
    const onLogoutListener = (e: CustomEvent) => {
      if (logoutClicked) logoutClicked(e);
    };
    const globalHeaderReference = globalHeaderRef?.current;
    globalHeaderReference?.addEventListener('on-logout-click', onLogoutListener);
    return () => globalHeaderReference?.removeEventListener('on-logout-click', onLogoutListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onLoginListener = (e: CustomEvent) => {
      if (loginClicked) loginClicked(e);
    };
    const globalHeaderReference = globalHeaderRef?.current;
    globalHeaderReference?.addEventListener('on-login-click', onLoginListener);
    return () => globalHeaderReference?.removeEventListener('on-login-click', onLoginListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    PrerenderQueue.add('global-header');

    const handleReady = () => {
      PrerenderQueue.remove('global-header');
      document.body.querySelector('#preloader')?.remove();
    };

    if (globalHeaderRef?.current?.shadowRoot) {
      handleReady();
      return;
    }

    globalHeaderRef?.current?.addEventListener('ready', () => handleReady());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleNavigate = (evt: CustomEvent) => {
      const { href } = evt.detail;
      history.push(href);
    };

    const handleOpenModal = (evt: CustomEvent) => {
      evt.preventDefault();
      const { linkText } = evt?.detail as any;
      setIsModalOpen(true);
      setFormModalLinkText(linkText);
    };

    globalHeaderRef?.current?.addEventListener('navigate', (e: CustomEvent) => handleNavigate(e));
    globalHeaderRef?.current?.addEventListener('opencontactform', (e: CustomEvent) =>
      handleOpenModal(e),
    );
    document.addEventListener('lockheader', () => setLocked(true));
    document.addEventListener('unlockheader', () => setLocked(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    globalHeaderRef.current.locked = locked;
  }, [locked]);

  useEffect(() => {
    globalHeaderRef.current.loading = isLoading;
  }, [isLoading]);

  useEffect(() => {
    globalHeaderRef.current.hasprogressbar = hasProgressBar;
  }, [hasProgressBar]);

  useEffect(() => {
    globalHeaderRef.current.transparent = isTransparent;
    globalHeaderRef.current.headerheight = isTransparent ? 0 : 'auto';
  }, [isTransparent]);

  const handleSuccess = () => {
    setIsModalOpen(false);
  };

  return (
    <div data-testid="qa-globalHeader">
      <gntc-global-header
        transparent={locked ? isTransparent : undefined}
        spabaseurl={SPA_BASE_URL}
        cmsbaseurl={CMS_BASE_URL}
        searchbaseurl="/search"
        // hideNavigation={navVisible}
        config={config ? JSON.stringify(config) : '{}'}
        labels={labels ? JSON.stringify(labels) : '{}'}
        menulist={menuList ? JSON.stringify(menuList) : '[]'}
        loginsection={loginSection ? JSON.stringify(loginSection) : '{}'}
        linklisttitlestyle={linkListTitleStyle}
        languagelist={languageList ? JSON.stringify(languageList) : '[]'}
        searchtoken={elasticsearchConfig?.search_key || ''}
        searchengine={elasticsearchConfig?.global_engine || ''}
        searchAuthengine={elasticsearchConfig?.global_auth_engine || ''}
        ref={globalHeaderRef}
        campaign={campaign || null}
        showCustomAccountSlotData={showCustomAccountSlotData}
        accountSection={parseProp(accountSection)}
        appsSection={parseProp(appsSection)}
        datalayerVariant={datalayerVariant}
        notificationConfig={parseProp(notificationConfig)}
        languageConfig={parseProp(languageConfig)}
        sessionConfig={parseProp(sessionConfig)}
        hideMobileNavigationButton={hideMobileNavigationButton}
        searchSection={searchSection ? JSON.stringify(searchSection) : '{}'}
        ctaLink={ctaLink ? JSON.stringify(ctaLink) : '{}'}
      >
        {children}
        <div slot="gntc-login-section-unauthorized">
          <UnauthorizedMessageOnHeader variant="header" />
        </div>
        <div slot="gnct-notifications-content-slot" data-header-dropdown-content="true">
          <NotificationDataWrapped onHasUnreadNotificationsChange={setHasUnreadNotifications} />
        </div>
      </gntc-global-header>

      {ctaLink?.marketoLink && (
        <MarketoFormModal
          baseUrl={marketoBaseUrl}
          formId={ctaLink?.marketoLink.formId}
          formDescription={ctaLink?.marketoLink.formDescription}
          munchkinId={marketoMunchkinId}
          rules={ctaLink?.marketoLink.rules}
          heading={ctaLink?.marketoLink.heading}
          recaptchaSitekey={recaptchaSitekey}
          recaptchaLegal={recaptchaLegal}
          onSuccess={handleSuccess}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          confirmationMessage={ctaLink?.marketoLink.confirmationMessage}
          emailFieldname={emailFieldname}
          emailBlacklistErrorMessage={emailBlacklistErrorMessage}
          emailCRMFieldname={emailCRMFieldname}
          emailsBlacklisted={ctaLink?.marketoLink.emailsBlacklisted}
          CRMSyncFieldnames={ctaLink?.marketoLink.CRMSyncFieldnames}
          isOpenedByCTAVariant="button"
          datalayerPosition="primary_nav"
          datalayerLinkText={formModalLinkText}
        />
      )}
    </div>
  );
};

export default memo(GlobalHeader);

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'gntc-global-header': any;
    }
  }
}
